import { useEffect } from 'react';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import { WebhookConfigRow } from 'components/settings/internalConfig/WebhookConfigRow';
import { useProcessorStatusMappers } from './hooks/useProcessorStatusMappers';
import { useWebhookConfigs } from './hooks/useWebhookConfigs';
import { useWebhookActions } from './hooks/useWebhookActions';

const OtherConfigs = (): JSX.Element => {
  const {
    paymentConfig,
    updateGlobalCardReaderValues,
    saveGlobalCardReaderValues,
    toFetchCardReaderValues,
    toFetchPaymentSolutions,
  } = usePaymentConfig();

  useEffect(() => {
    toFetchPaymentSolutions();
    toFetchCardReaderValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Get MOCK processors
  const processors = paymentConfig.paymentSolutions?.processor?.processors;

  // Fetch status mappers for processors
  const mockProcessors =
    processors?.filter(
      (processor) =>
        processor.isEnabled &&
        processor.processorName?.toUpperCase().startsWith('MOCK')
    ) || [];

  const processorStatusMappers = useProcessorStatusMappers(mockProcessors);

  // Build webhook configs
  const webhookConfigs = useWebhookConfigs({
    processors,
    cardReaderValues: paymentConfig.getCardReaderValues.items,
    processorStatusMappers,
  });

  // Handle webhook actions
  const { handleToggleChange, handleWebhookStatusChange } = useWebhookActions({
    cardReaderValues: paymentConfig.getCardReaderValues.items,
    processorConfigs: webhookConfigs,
    updateGlobalCardReaderValues,
    saveGlobalCardReaderValues,
    toFetchCardReaderValues,
  });

  return (
    <div className="py-4 max-w-3xl">
      <h2 className="text-xl font-semibold text-gray-blue-80 leading-7">
        Bypass Webhook Callback
      </h2>
      <p className="font-normal text-sm leading-5 text-gray-blue-70">
        Payments will automatically process without requiring actual
        device/webhook callback
      </p>

      {webhookConfigs.length > 0 ? (
        <div className="mt-4">
          {/* Table Header */}
          <div className="flex items-center pb-3 border-b border-gray-200">
            <div className="w-3/10">
              <span className="text-sm font-semibold text-gray-blue-80 leading-5">
                Processor Name
              </span>
            </div>
            <div className="w-4/10">
              <span className="text-sm font-semibold text-gray-blue-80 leading-5">
                Webhook Bypass Status
              </span>
            </div>
            <div className="w-3/10 text-right">
              <span className="text-sm font-semibold text-gray-blue-80 leading-5">
                Toggle
              </span>
            </div>
          </div>

          {/* Table Rows */}
          {webhookConfigs.map((config) => (
            <WebhookConfigRow
              key={config.processorId}
              config={config}
              onToggle={handleToggleChange}
              onStatusChange={handleWebhookStatusChange}
            />
          ))}
        </div>
      ) : (
        <div className="mt-4 text-center">
          <p className="text-gray-blue-70">
            No MOCK processors found. MOCK processors are required to use this
            feature.
          </p>
        </div>
      )}
    </div>
  );
};

export default OtherConfigs;
